import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import {
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  ExternalLink,
  Search,
  GitBranch,
  Layers,
} from "lucide-react";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import {
  canCreateThreatModel,
  canEditThreatModel,
  type ThreatModelVersion,
} from "../types";
import ThreatModelVersionDialog from "./ThreatModelVersionDialog";

interface FindingsSummary {
  total: number;
  bySeverity: { Critical: number; High: number; Medium: number; Low: number };
  byCategory: Record<string, number>;
}

export interface ThreatModel {
  id: string;
  family_id?: string;
  name: string;
  version?: string;
  phase: string;
  status: string;
  findings_summary?: FindingsSummary;
  product_id?: string;
  created_by_id?: string;
  updated_at: string;
  archived_at?: string | null;
  family?: { id: string; name: string; created_by_id: string };
  results_inherited?: boolean;
  inherited_from?: { id: string; version: string };
}

function versionedName(tm: ThreatModel): string {
  const name = tm.family?.name ?? tm.name;
  return tm.version ? `${name} - ${tm.version}` : name;
}

interface ProductThreatModelPanelProps {
  productId: string;
}

const EMPTY_SUMMARY = { Critical: 0, High: 0, Medium: 0, Low: 0 };

export function toItems(resp: any): ThreatModel[] {
  const d = resp?.data?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  return [];
}

const SEV_BADGE: Record<string, string> = {
  Critical: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  High: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  Medium:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  Low: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
};

export default function ProductThreatModelPanel({
  productId,
}: ProductThreatModelPanelProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const [showLink, setShowLink] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [changeVersionOf, setChangeVersionOf] = useState<ThreatModel | null>(
    null,
  );
  const [newVersionOf, setNewVersionOf] = useState<ThreatModel | null>(null);

  const { data: linkedResp } = useQuery(
    ["product-threat-models", productId],
    () => apiService.getThreatModels({ product_id: productId }),
  );
  const linked = toItems(linkedResp);

  // Unlinked models for the link dialog
  const { data: allResp } = useQuery(
    ["threat-models-unlinked", linkSearch],
    () =>
      apiService.getThreatModels(
        linkSearch ? { search: linkSearch } : undefined,
      ),
    { enabled: showLink },
  );
  // A product pins at most one version per family; switching is done with
  // "Change version" on the linked row.
  const pinnedFamilies = new Set(linked.map((tm) => tm.family_id));
  const unlinked = toItems(allResp).filter(
    (tm) =>
      !tm.product_id &&
      !pinnedFamilies.has(tm.family_id) &&
      canEditThreatModel(user, tm.created_by_id),
  );

  const linkMutation = useMutation(
    (tmId: string) =>
      apiService.updateThreatModel(tmId, { product_id: productId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product-threat-models", productId]);
        queryClient.invalidateQueries(["threat-models-unlinked"]);
        setShowLink(false);
        setLinkSearch("");
        toast.success("Threat model linked");
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.error || "Failed to link threat model",
        );
      },
    },
  );

  const unlinkMutation = useMutation(
    (tmId: string) => apiService.updateThreatModel(tmId, { product_id: "" }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product-threat-models", productId]);
        toast.success("Threat model unlinked");
      },
      onError: () => {
        toast.error("Failed to unlink threat model");
      },
    },
  );

  // Aggregate findings across the pinned version of each linked family
  const totals = useMemo(() => {
    const agg = { ...EMPTY_SUMMARY };
    for (const tm of linked) {
      const s = tm.findings_summary?.bySeverity;
      if (!s) continue;
      agg.Critical += s.Critical ?? 0;
      agg.High += s.High ?? 0;
      agg.Medium += s.Medium ?? 0;
      agg.Low += s.Low ?? 0;
    }
    return agg;
  }, [linked]);

  const grandTotal = totals.Critical + totals.High + totals.Medium + totals.Low;

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
      <div className="px-6 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100"
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          <ShieldAlert className="h-4 w-4" />
          Threat Modelling
          <span className="text-xs font-normal text-gray-500">
            ({linked.length})
          </span>
        </button>
        <div className="flex items-center gap-3">
          {grandTotal > 0 && (
            <div className="flex items-center gap-1.5">
              {(["Critical", "High", "Medium", "Low"] as const).map((sev) =>
                totals[sev] > 0 ? (
                  <span
                    key={sev}
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SEV_BADGE[sev]}`}
                  >
                    {totals[sev]} {sev}
                  </span>
                ) : null,
              )}
            </div>
          )}
          <Link
            to="/threat-modelling"
            className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            View all <ExternalLink className="h-3.5 w-3.5" />
          </Link>
          {canCreateThreatModel(user) && (
            <button
              type="button"
              onClick={() => {
                setExpanded(true);
                setShowLink((v) => !v);
              }}
              className="btn btn-primary text-sm flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" /> Link
            </button>
          )}
        </div>
      </div>

      {expanded && showLink && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search threat models..."
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              className="input pl-10 w-full text-sm"
            />
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {unlinked.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                No unlinked threat models found.
              </p>
            ) : (
              unlinked.map((tm) => (
                <button
                  key={tm.id}
                  onClick={() => linkMutation.mutate(tm.id)}
                  disabled={linkMutation.isLoading}
                  className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center"
                >
                  <span>{versionedName(tm)}</span>
                  <Plus className="h-3.5 w-3.5 text-gray-400" />
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Linked models list */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
          {linked.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No threat models linked yet.
            </p>
          ) : (
            linked.map((tm) => (
              <div
                key={tm.id}
                className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/threat-modelling/${tm.id}`}
                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {versionedName(tm)}
                  </Link>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap items-center gap-1.5">
                    <span>
                      {tm.phase} · {tm.status}
                    </span>
                    {tm.results_inherited && (
                      <span className="text-amber-700 dark:text-amber-300">
                        ·{" "}
                        {tm.inherited_from
                          ? `Results inherited from ${tm.inherited_from.version}`
                          : "Inherited results"}
                      </span>
                    )}
                    {tm.archived_at && <span>· Archived</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canEditThreatModel(user, tm.created_by_id) && (
                    <button
                      type="button"
                      onClick={() => setChangeVersionOf(tm)}
                      className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                      title="Change version"
                    >
                      <Layers className="h-4 w-4" />
                    </button>
                  )}
                  {canCreateThreatModel(user) && (
                    <button
                      type="button"
                      onClick={() => setNewVersionOf(tm)}
                      className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                      title="New version"
                    >
                      <GitBranch className="h-4 w-4" />
                    </button>
                  )}
                  {tm.findings_summary?.bySeverity &&
                    (["Critical", "High", "Medium", "Low"] as const).map(
                      (sev) =>
                        tm.findings_summary!.bySeverity[sev] > 0 ? (
                          <span
                            key={sev}
                            className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs font-bold ${SEV_BADGE[sev]}`}
                            title={sev}
                          >
                            {tm.findings_summary!.bySeverity[sev]}
                          </span>
                        ) : null,
                    )}
                  {canEditThreatModel(user, tm.created_by_id) && (
                    <button
                      onClick={() => unlinkMutation.mutate(tm.id)}
                      disabled={unlinkMutation.isLoading}
                      className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Unlink"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {changeVersionOf && (
        <ThreatModelPinDialog
          productId={productId}
          pinned={changeVersionOf}
          onClose={() => setChangeVersionOf(null)}
        />
      )}
      {newVersionOf && (
        <ThreatModelVersionDialog
          anchorId={newVersionOf.id}
          familyName={newVersionOf.family?.name ?? newVersionOf.name}
          defaultSourceId={
            newVersionOf.archived_at ? undefined : newVersionOf.id
          }
          onClose={() => setNewVersionOf(null)}
        />
      )}
    </div>
  );
}

interface ThreatModelPinDialogProps {
  productId: string;
  pinned: ThreatModel;
  onClose: () => void;
}

function ThreatModelPinDialog({
  productId,
  pinned,
  onClose,
}: ThreatModelPinDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(pinned.id);

  const { data: versionsResp, isLoading } = useQuery(
    ["threat-model-versions", pinned.id],
    () => apiService.getThreatModelVersions(pinned.id),
  );
  const versions: ThreatModelVersion[] = versionsResp?.data?.data ?? [];
  // Archived versions are not offered for new links; a version linked to
  // another product cannot be pinned here.
  const candidates = versions.filter(
    (v) =>
      v.id === pinned.id ||
      (!v.archived_at &&
        (!v.product_id || v.product_id === productId) &&
        canEditThreatModel(user, v.created_by_id)),
  );

  const pinMutation = useMutation(
    () => apiService.pinThreatModelVersion(selectedId, productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product-threat-models", productId]);
        queryClient.invalidateQueries(["threat-model-versions"]);
        queryClient.invalidateQueries(["threat-model-families"]);
        toast.success("Linked version changed");
        onClose();
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.error || "Failed to change linked version",
        );
      },
    },
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Change version
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {pinned.family?.name ?? pinned.name}: choose the version this
            product uses. Neither version is reset or rerun.
          </p>
          {isLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading versions...
            </p>
          ) : (
            <div className="space-y-1">
              {candidates.map((v) => (
                <label
                  key={v.id}
                  className="flex items-start gap-2 rounded px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <input
                    type="radio"
                    name="tm-pin-version"
                    className="mt-1"
                    checked={selectedId === v.id}
                    onChange={() => setSelectedId(v.id)}
                  />
                  <span>
                    <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
                      {v.version}
                    </span>
                    {v.id === pinned.id && (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        (current)
                      </span>
                    )}
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {v.phase} · {v.status}
                      {v.results_inherited &&
                        ` · ${
                          v.inherited_from
                            ? `Results inherited from ${v.inherited_from.version}`
                            : "Inherited results"
                        }`}
                      {v.archived_at && " · Archived"}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex justify-end gap-2 rounded-b-lg">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => pinMutation.mutate()}
            disabled={selectedId === pinned.id || pinMutation.isLoading}
            className="btn btn-primary disabled:opacity-50"
          >
            {pinMutation.isLoading ? "Saving..." : "Use this version"}
          </button>
        </div>
      </div>
    </div>
  );
}
